import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import Button from '../../components/ui/Button';
import { Users, Clock, BookOpen, Search, Filter, Mail, Phone, MessageSquare, Calendar, Target, X, CheckCircle, Trash2 } from 'lucide-react';
import { formatTimeSlot } from '../../utils/dateUtils';

// Helper functions for group matching
const findSharedSlots = (students) => {
  if (students.length === 0) return [];
  const allSlots = students[0].availabilities;
  return allSlots.filter(slot =>
    students.every(student => student.availabilities.includes(slot))
  );
};

const calculateCompatibility = (students, sharedSlots) => {
  let score = 0;
  
  // Shared slots (max 50 points)
  score += Math.min(50, sharedSlots.length * 15);
  
  // Group size bonus (max 20 points)
  if (students.length >= 3) score += 15;
  if (students.length >= 4) score += 5;
  
  // Wait time urgency (max 20 points)
  const avgWaitDays = students.reduce((sum, s) => sum + s.daysSinceSubmitted, 0) / students.length;
  if (avgWaitDays > 30) score += 10;
  if (avgWaitDays > 60) score += 10;
  
  // Same exact goal level (10 points)
  const allSameLevel = students.every(s => s.goal_level === students[0].goal_level);
  if (allSameLevel) score += 10;
  
  return Math.min(100, score);
};

const findPotentialGroups = (students, filters = {}) => {
  const seekingStudents = students.filter(s => 
    s.status === 'available' || s.status === 'long_wait'
  );

  // Group by class type and goal level
  const groupsByTypeAndLevel = {};
  
  seekingStudents.forEach(student => {
    const levelKey = student.goal_level || student.cantonese_level || 'conversational';
    const key = `${student.classTypeId}-${student.goal}-${levelKey}`;
    if (!groupsByTypeAndLevel[key]) {
      groupsByTypeAndLevel[key] = [];
    }
    groupsByTypeAndLevel[key].push(student);
  });

  const potentialGroups = [];
  const minSize = filters.minGroupSize || 2;
  const maxSize = filters.maxGroupSize || 4;

  Object.entries(groupsByTypeAndLevel).forEach(([key, studentsInGroup]) => {
    if (studentsInGroup.length < minSize) return;

    // Find all possible combinations
    const combinations = getCombinations(studentsInGroup, minSize, maxSize);

    combinations.forEach(combo => {
      const sharedSlots = findSharedSlots(combo);
      
      const minSharedSlots = filters.minSharedSlots || 2;
      if (sharedSlots.length >= minSharedSlots) {
        const compatibilityScore = calculateCompatibility(combo, sharedSlots);
        
        const levelKey = combo[0].goal_level || combo[0].cantonese_level || 'Conversational';
        const goalLevel = combo[0].goal === 'hsk' 
          ? `HSK Level ${levelKey}`
          : combo[0].cantonese_level
          ? `Cantonese - ${levelKey.charAt(0).toUpperCase() + levelKey.slice(1)}`
          : 'Conversational';
        
        potentialGroups.push({
          students: combo,
          sharedSlots,
          compatibilityScore,
          classTypeId: combo[0].classTypeId,
          classTypeName: combo[0].classTypeName,
          goalLevel,
          minSize,
          idealSize: maxSize,
          priority: compatibilityScore >= 90 ? 'ready' : compatibilityScore >= 75 ? 'almost' : 'possible'
        });
      }
    });
  });

  // Sort by priority: compatibility score, then wait time
  return potentialGroups.sort((a, b) => {
    if (b.compatibilityScore !== a.compatibilityScore) {
      return b.compatibilityScore - a.compatibilityScore;
    }
    const aMaxWait = Math.max(...a.students.map(s => s.daysSinceSubmitted));
    const bMaxWait = Math.max(...b.students.map(s => s.daysSinceSubmitted));
    return bMaxWait - aMaxWait;
  });
};

const getCombinations = (arr, min, max) => {
  const result = [];
  
  const generate = (start, combo) => {
    if (combo.length >= min && combo.length <= max) {
      result.push([...combo]);
    }
    if (combo.length >= max) return;
    
    for (let i = start; i < arr.length; i++) {
      combo.push(arr[i]);
      generate(i + 1, combo);
      combo.pop();
    }
  };
  
  generate(0, []);
  return result;
};

// CreateGroupModal Component
const CreateGroupModal = ({ group, onClose, onConfirm, allTutors }) => {
  const [selectedSlot, setSelectedSlot] = useState(group.sharedSlots[0]);
  const [selectedTutor, setSelectedTutor] = useState('');
  const [step, setStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [availableTutors, setAvailableTutors] = useState([]);

  // Filter tutors when slot changes
  useEffect(() => {
    if (selectedSlot && allTutors) {
      const filtered = filterTutorsForSlot(selectedSlot, group.classTypeId, allTutors);
      setAvailableTutors(filtered);
      // Reset tutor selection if current selection is not in filtered list
      if (selectedTutor && !filtered.find(t => t.$id === selectedTutor)) {
        setSelectedTutor('');
      }
    }
  }, [selectedSlot, group.classTypeId, allTutors]);

  const filterTutorsForSlot = (slot, classTypeId, tutorList) => {
    if (!tutorList || tutorList.length === 0) return [];

    // Parse the slot (e.g., "Monday-7pm" -> check if tutor is available Monday at 7pm)
    const [dayName, timeStr] = slot.split('-');
    
    // Convert timeStr to format used in tutor availability (e.g., "7pm" -> "7:00 PM")
    const convertTimeFormat = (time) => {
      // Handle formats like "7pm", "7:30pm", "8am", "8:30am"
      const match = time.match(/^(\d+):?(\d*)([ap]m)$/i);
      if (!match) return null;
      
      const [, hour, minutes, period] = match;
      const formattedHour = parseInt(hour);
      const formattedMinutes = minutes || '00';
      const formattedPeriod = period.toUpperCase();
      
      return `${formattedHour}:${formattedMinutes} ${formattedPeriod}`;
    };

    const formattedTime = convertTimeFormat(timeStr);
    if (!formattedTime) return [];

    const availabilityKey = `${dayName}-${formattedTime}`;

    return tutorList.filter(tutor => {
      // Check if tutor is approved
      if (tutor.status !== 'approved') return false;

      // Check if tutor teaches this class type
      // Note: You may need to map classTypeId to curriculum codes
      // For now, we'll include all approved tutors
      // TODO: Add class type matching logic based on tutor's curriculums

      // Check if tutor is available at this time
      if (!tutor.availability) return false;
      
      try {
        const availability = typeof tutor.availability === 'string' 
          ? JSON.parse(tutor.availability) 
          : tutor.availability;
        
        return availability[availabilityKey] === true;
      } catch (e) {
        console.error('Error parsing tutor availability:', e);
        return false;
      }
    });
  };

  const handleConfirm = async () => {
    setIsCreating(true);
    setError(null);
    
    try {
      // Parse the selected time slot (e.g., "Monday-7pm" -> day: "Monday", time: "7pm")
      const [day, time] = selectedSlot.split('-');
      
      // Prepare initial members array in the format expected by the API
      const initialMembers = group.students.map(student => 
        JSON.stringify({
          userId: student.userId || student.availabilityId, // TODO: Use actual userId
          name: student.name,
          joinedAt: new Date().toISOString()
        })
      );
      
      // Prepare the class data
      const classData = {
        classTypeId: group.classTypeId,
        classTypeName: group.classTypeName,
        goalLevel: group.goalLevel,
        day: day,
        time: time,
        timeSlot: selectedSlot,
        totalSpots: group.students.length, // Or set a larger number if you want room for more
        initialMembers: initialMembers,
        students: group.students,
        tutor: availableTutors.find(t => t.$id === selectedTutor),
        studentAvailabilityIds: group.students.map(s => s.availabilityId)
      };
      
      // Call the parent handler which will make the API call
      await onConfirm(classData);
      
      setIsCreating(false);
      setIsSuccess(true);
      
      // Close after showing success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error creating class:', err);
      setIsCreating(false);
      setError(err.message || 'Failed to create class');
    }
  };

  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg max-w-md w-full p-8 text-center">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Class Created!</h3>
          <p className="text-gray-300">
            Students and tutor have been notified.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Create Group Class</h2>
            <p className="text-sm text-gray-400 mt-1">
              {group.classTypeName} • {group.goalLevel}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            disabled={isCreating}
          >
            <X size={24} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                1
              </div>
              <span className={step >= 1 ? 'text-white' : 'text-gray-400'}>
                Select Time
              </span>
            </div>
            
            <div className="flex-1 h-0.5 bg-gray-700 mx-4"></div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                2
              </div>
              <span className={step >= 2 ? 'text-white' : 'text-gray-400'}>
                Assign Tutor
              </span>
            </div>
            
            <div className="flex-1 h-0.5 bg-gray-700 mx-4"></div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 3 ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                3
              </div>
              <span className={step >= 3 ? 'text-white' : 'text-gray-400'}>
                Confirm
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-900/20 border border-red-700 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {step === 1 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">
                Select Weekly Class Time
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                This group has {group.sharedSlots.length} compatible time slots. Choose one for the weekly class.
              </p>
              
              <div className="space-y-3">
                {group.sharedSlots.map((slot, idx) => (
                  <label
                    key={idx}
                    className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedSlot === slot
                        ? 'border-purple-500 bg-purple-900/20'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="timeSlot"
                      value={slot}
                      checked={selectedSlot === slot}
                      onChange={(e) => setSelectedSlot(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="text-white font-medium">{formatTimeSlot(slot)}</p>
                      <p className="text-sm text-gray-400">
                        All {group.students.length} students available
                      </p>
                    </div>
                    {selectedSlot === slot && (
                      <CheckCircle size={20} className="text-purple-400" />
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">
                Assign a Tutor
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Select a tutor who is available at <span className="text-purple-400 font-medium">{formatTimeSlot(selectedSlot)}</span>
              </p>
              
              {availableTutors.length === 0 ? (
                <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 text-center">
                  <p className="text-yellow-400 text-sm">
                    No approved tutors are available at this time slot.
                  </p>
                  <p className="text-gray-400 text-xs mt-2">
                    Try selecting a different time slot or manually assign a tutor later.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableTutors.map((tutor) => (
                    <label
                      key={tutor.$id}
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedTutor === tutor.$id
                          ? 'border-purple-500 bg-purple-900/20'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="tutor"
                        value={tutor.$id}
                        checked={selectedTutor === tutor.$id}
                        onChange={(e) => setSelectedTutor(e.target.value)}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <p className="text-white font-medium">
                          {tutor.firstName} {tutor.lastName}
                        </p>
                        <p className="text-sm text-gray-400">
                          {tutor.yearsExperience} years experience
                          {tutor.teachingStyles && tutor.teachingStyles.length > 0 && 
                            ` • ${tutor.teachingStyles.slice(0, 2).join(', ')}`
                          }
                        </p>
                      </div>
                      {selectedTutor === tutor.$id && (
                        <CheckCircle size={20} className="text-purple-400" />
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">
                Confirm Class Details
              </h3>
              
              <div className="space-y-4">
                {/* Class Info */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-2">Class Details</p>
                  <p className="text-white font-medium mb-1">
                    {group.classTypeName} • {group.goalLevel}
                  </p>
                  <p className="text-purple-400 flex items-center">
                    <Clock size={14} className="mr-2" />
                    {formatTimeSlot(selectedSlot)}
                  </p>
                </div>

                {/* Tutor */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-2">Assigned Tutor</p>
                  {selectedTutor ? (
                    <p className="text-white font-medium">
                      {availableTutors.find(t => t.$id === selectedTutor)?.firstName}{' '}
                      {availableTutors.find(t => t.$id === selectedTutor)?.lastName}
                    </p>
                  ) : (
                    <p className="text-gray-400 text-sm">No tutor selected</p>
                  )}
                </div>

                {/* Students */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-3">
                    Students ({group.students.length})
                  </p>
                  <div className="space-y-2">
                    {group.students.map(student => (
                      <div
                        key={student.availabilityId}
                        className="flex items-center justify-between py-2"
                      >
                        <div>
                          <p className="text-white text-sm font-medium">{student.name}</p>
                          <p className="text-xs text-gray-400">{student.email}</p>
                        </div>
                        {student.daysSinceSubmitted > 30 && (
                          <span className="text-xs text-yellow-400 flex items-center">
                            <Clock size={12} className="mr-1" />
                            {Math.floor(student.daysSinceSubmitted / 7)} weeks waiting
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* What happens next */}
                <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-300 mb-2">
                    What happens next:
                  </p>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>✓ Class will be created in the system</li>
                    <li>✓ All students will be marked as "enrolled"</li>
                    <li>✓ Confirmation emails sent to students</li>
                    <li>✓ Assignment notification sent to tutor</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-6 flex items-center justify-between">
          <button
            onClick={() => {
              if (step === 1) {
                onClose();
              } else {
                setStep(step - 1);
              }
            }}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            disabled={isCreating}
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          <button
            onClick={() => {
              if (step === 3) {
                handleConfirm();
              } else {
                setStep(step + 1);
              }
            }}
            disabled={(step === 2 && availableTutors.length > 0 && !selectedTutor) || isCreating}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isCreating ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              step === 3 ? 'Create Class' : 'Next'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// GroupCard Component
const GroupCard = ({ group, onExpand, isExpanded, onCreateGroup }) => {
  const getPriorityStyles = () => {
    if (group.priority === 'ready') {
      return {
        border: 'border-green-500',
        bg: 'bg-green-900/10',
        badge: { color: 'bg-green-500', text: 'Ready to Form', icon: '✓' }
      };
    }
    if (group.priority === 'almost') {
      return {
        border: 'border-yellow-500',
        bg: 'bg-yellow-900/10',
        badge: { color: 'bg-yellow-500', text: `Needs ${group.idealSize - group.students.length} more`, icon: '⚡' }
      };
    }
    return {
      border: 'border-blue-500',
      bg: 'bg-blue-900/10',
      badge: { color: 'bg-blue-500', text: 'Possible Match', icon: '○' }
    };
  };

  const styles = getPriorityStyles();
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getGoalDisplay = (student) => {
    if (student.cantonese_level) {
      return `Cantonese - ${student.cantonese_level.charAt(0).toUpperCase() + student.cantonese_level.slice(1)}`;
    }
    if (student.goal === 'hsk') {
      return `HSK Level ${student.goal_level}`;
    }
    if (student.goal === 'exam') {
      return `${student.exam_type} - ${student.exam_year_phase}`;
    }
    if (student.goal === 'conversational') {
      return 'Light Conversational (no homework)';
    }
    return 'Not specified';
  };

  return (
    <div className={`rounded-lg border-2 ${styles.border} ${styles.bg} p-6 transition-all`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-bold text-white">
              {group.classTypeName} • {group.goalLevel}
            </h3>
            <span className={`${styles.badge.color} text-white text-xs px-3 py-1 rounded-full font-medium`}>
              {styles.badge.icon} {styles.badge.text}
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-400">
            <span className="flex items-center">
              <Users size={14} className="mr-1" />
              {group.students.length} students
            </span>
            <span className="flex items-center">
              <Clock size={14} className="mr-1" />
              {group.sharedSlots.length} shared slots
            </span>
            <span className="flex items-center">
              <Target size={14} className="mr-1" />
              {group.compatibilityScore}% compatible
            </span>
          </div>
        </div>
      </div>

      {/* Student Pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {group.students.map(student => (
          <div
            key={student.availabilityId}
            className="bg-purple-600/30 border border-purple-500 rounded-full px-4 py-2 text-sm flex items-center space-x-2"
          >
            <span className="font-medium">{student.name}</span>
            {student.daysSinceSubmitted > 30 && (
              <span className="text-yellow-400 text-xs">⏰</span>
            )}
          </div>
        ))}
      </div>

      {/* Shared Availability */}
      <div className="bg-gray-900/50 rounded p-3 mb-4">
        <p className="text-purple-300 text-sm mb-2 flex items-center">
          <Calendar size={14} className="mr-2" />
          Shared Availability
        </p>
        <div className="flex flex-wrap gap-2">
          {group.sharedSlots.slice(0, isExpanded ? undefined : 4).map((slot, idx) => (
            <span
              key={idx}
              className="bg-indigo-600 text-white text-xs px-3 py-1 rounded font-mono"
            >
              {formatTimeSlot(slot)}
            </span>
          ))}
          {!isExpanded && group.sharedSlots.length > 4 && (
            <span className="text-gray-400 text-xs px-3 py-1">
              +{group.sharedSlots.length - 4} more
            </span>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-gray-700 pt-4 mt-4 space-y-4">
          {group.students.map(student => (
            <div key={student.availabilityId} className="bg-gray-800 rounded p-3">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-medium text-white">{student.name}</p>
                  <p className="text-xs text-gray-400">{student.email}</p>
                </div>
                <span className="text-xs text-gray-500">
                  Submitted {formatDate(student.submittedAt)}
                </span>
              </div>
              
              <div className="text-xs text-gray-400 space-y-1">
                <p>Goal: {getGoalDisplay(student)}</p>
                <p>Available: {student.availabilities.length} time slots</p>
              </div>
            </div>
          ))}

          <div className="bg-gray-800 rounded p-3">
            <p className="text-sm font-medium mb-2 text-purple-300">Why this group works:</p>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>✓ Same class type ({group.classTypeName})</li>
              <li>✓ Same goal level ({group.goalLevel})</li>
              <li>✓ {group.sharedSlots.length} overlapping time slots</li>
              {group.students.length >= 3 && <li>✓ Ideal group size reached</li>}
            </ul>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center space-x-3 mt-4">
        <button
          onClick={onCreateGroup}
          className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 px-6 py-3 rounded-lg font-medium flex items-center justify-center transition-all"
        >
          <Users size={18} className="mr-2" />
          Create Group Class
        </button>
        <button
          onClick={onExpand}
          className="border border-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg transition-colors"
        >
          {isExpanded ? 'Less' : 'More'} Details
        </button>
      </div>
    </div>
  );
};

const StudentsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('groups');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    classType: 'all',
    status: 'all',
    search: ''
  });

  // Group formation filters
  const [groupFilters, setGroupFilters] = useState({
    minGroupSize: 2,
    maxGroupSize: 4,
    minSharedSlots: 2,
    strictLevelMatch: true,
    classType: 'all'
  });

  // Check admin status on mount
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const adminStatus = await api.admin.verifyAdmin(user.$id);
        if (!adminStatus) {
          navigate('/classes');
          return;
        }
        setIsAdmin(true);
      } catch (err) {
        console.error('Error checking admin status:', err);
        navigate('/classes');
      }
    };

    checkAdminStatus();
  }, [user, navigate]);

  // Load data when admin status is confirmed
  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin]);

  // Apply filters when students or filters change
  useEffect(() => {
    applyFilters();
  }, [students, filters]);

  //Add a click-away reset
  useEffect(() => {
  if (!pendingDeleteId) return;
  const reset = (e) => {
    if (!e.target.closest('[data-delete-btn]')) setPendingDeleteId(null);
  };
  document.addEventListener('mousedown', reset);
  return () => document.removeEventListener('mousedown', reset);
}, [pendingDeleteId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load students, class types, and tutors in parallel
      const [studentsData, classTypesData, tutorsData] = await Promise.all([
        api.availability.getAllStudentsWithAvailability(),
        api.classTypes.getActive(),
        api.tutors.getApplications('approved') // Only get approved tutors
      ]);

      setStudents(studentsData);
      setClassTypes(classTypesData);
      setTutors(tutorsData);
      
    } catch (err) {
      console.error('Error loading students data:', err);
      setError('Failed to load students data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...students];

    // Filter by class type
    if (filters.classType !== 'all') {
      filtered = filtered.filter(student => 
        student.classTypeId === filters.classType
      );
    }

    // Filter by status
    if (filters.status !== 'all') {
      filtered = filtered.filter(student => student.status === filters.status);
    }

    // Filter by search term
    if (filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(student =>
        student.name.toLowerCase().includes(searchTerm) ||
        student.email.toLowerCase().includes(searchTerm) ||
        student.classTypeName.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredStudents(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleGroupFilterChange = (key, value) => {
    setGroupFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDeleteStudent = async (student) => {
    if (pendingDeleteId !== student.availabilityId) {
      // First click — enter confirm mode
      setPendingDeleteId(student.availabilityId);
      return;
    }

    // Second click — confirmed, perform delete
    try {
      setDeletingId(student.availabilityId);
      setPendingDeleteId(null);
      await api.users.deleteStudent(student.userId);
      setStudents(prev => prev.filter(s => s.availabilityId !== student.availabilityId));
    } catch (err) {
      console.error('Error deleting student:', err);
      setError(`Failed to delete ${student.name}: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/classes');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const toggleExpand = (index) => {
    setExpandedGroups(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleCreateGroup = (group) => {
    setSelectedGroup(group);
    setShowModal(true);
  };

  const handleConfirmCreate = async (classData) => {
    try {
      // Create the class
      const response = await api.classes.create({
        classTypeId: classData.classTypeId,
        day: classData.day,
        time: classData.time,
        totalSpots: classData.totalSpots,
        initialMembers: classData.initialMembers
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create class');
      }

      console.log('Class created successfully:', response.classId);
      
      // TODO: Update student statuses to enrolled
      // This would typically be done in the backend or via a separate API call
      // await Promise.all(
      //   classData.studentAvailabilityIds.map(async (availId) => {
      //     await api.availability.updateStatus(availId, 'enrolled', response.classId);
      //   })
      // );
      
      // TODO: Send notifications
      // await api.notifications.sendClassCreationNotifications({
      //   students: classData.students,
      //   tutor: classData.tutor,
      //   classDetails: {
      //     classTypeName: classData.classTypeName,
      //     goalLevel: classData.goalLevel,
      //     day: classData.day,
      //     time: classData.time
      //   }
      // });
      
      // Refresh the students list
      await loadData();
      
      return response;
    } catch (error) {
      console.error('Error creating class:', error);
      throw error;
    }
  };

  const getGoalDisplay = (student) => {
    if (student.cantonese_level) {
      return `Cantonese - ${student.cantonese_level.charAt(0).toUpperCase() + student.cantonese_level.slice(1)}`;
    }
    
    if (student.goal === 'hsk') {
      return `HSK Level ${student.goal_level}`;
    }
    
    if (student.goal === 'exam') {
      return `${student.exam_type} - ${student.exam_year_phase}`;
    }
    
    if (student.goal === 'conversational') {
      return 'Light Conversational (no homework)';
    }
    
    return 'Not specified';
  };

  // Calculate stats
  const getStats = () => {
    const total = students.length;
    const enrolled = students.filter(s => s.status === 'enrolled').length;
    
    // A student is "seeking" if their status is 'available' OR 'long_wait'
    const seekingStudents = students.filter(s => s.status === 'available' || s.status === 'long_wait');
    const seeking = seekingStudents.length;
  
    // Calculate most popular day ONLY from students who are seeking
    const dayCount = {};
    seekingStudents.forEach(student => {
      student.availabilities.forEach(slot => {
        const day = slot.split('-')[0];
        dayCount[day] = (dayCount[day] || 0) + 1;
      });
    });
    
    const popularDay = Object.keys(dayCount).length > 0 
      ? Object.keys(dayCount).reduce((a, b) => dayCount[a] > dayCount[b] ? a : b)
      : 'N/A';
    
    return { total, enrolled, seeking, popularDay };
  };

  const stats = getStats();
  const groupFilteredStudents = groupFilters.classType === 'all'
    ? students
    : students.filter(s => s.classTypeId === groupFilters.classType);

  const potentialGroups = findPotentialGroups(groupFilteredStudents, groupFilters);

  // Get status display
  const getStatusDisplay = (status, daysSinceSubmitted) => {
    switch (status) {
      case 'available':
        return {
          color: 'text-green-400',
          text: 'Available',
          icon: '🟢'
        };
      case 'enrolled':
        return {
          color: 'text-blue-400', 
          text: 'In Class',
          icon: '🔶'
        };
      case 'long_wait':
        return {
          color: 'text-yellow-400',
          text: `Long Wait (${Math.floor(daysSinceSubmitted / 7)} weeks)`,
          icon: '⏰'
        };
      default:
        return {
          color: 'text-gray-400',
          text: 'Unknown',
          icon: '❓'
        };
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-purple-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-400">Loading students...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Modal */}
      {showModal && selectedGroup && (
        <CreateGroupModal
          group={selectedGroup}
          onClose={() => setShowModal(false)}
          onConfirm={handleConfirmCreate}
          allTutors={tutors}
        />
      )}

      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          {/* Left side: Title + Navigation */}
          <div className="flex items-center space-x-8">
            <h1 className="text-xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
                Admin Dashboard
              </span>
            </h1>
            
            {/* Navigation */}
            <div className="hidden sm:flex items-center space-x-6">
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Classes
              </button>
              <span className="text-purple-400 font-medium">Students</span>
              <button
                onClick={() => navigate('/admin/tutors')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Tutors
              </button>
            </div>
          </div>
          
          {/* Right side: User controls */}
          <div className="flex items-center space-x-4">
            <span className="text-gray-300 text-sm hidden sm:inline">
              {user?.name}
            </span>
            <Button
              variant="outline"
              size="small"
              onClick={handleLogout}
              className="border-gray-600 hover:bg-gray-700"
            >
              Logout
            </Button>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4">
        {error && (
          <div className="mb-6 p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Page title */}
        <div className="text-center mb-8">
          <div className="inline-block text-6xl font-light text-purple-400 opacity-20 mb-2">
            学生管理
          </div>
          <h2 className="text-2xl font-bold">Students</h2>
          <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-purple-400" />
              <div className="ml-4">
                <p className="text-gray-400 text-sm">Total Students</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </div>
        
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center">
              <BookOpen className="h-8 w-8 text-blue-400" />
              <div className="ml-4">
                <p className="text-gray-400 text-sm">Enrolled in Classes</p>
                <p className="text-2xl font-bold">{stats.enrolled}</p>
              </div>
            </div>
          </div>
        
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-green-400" />
              <div className="ml-4">
                <p className="text-gray-400 text-sm">Seeking Classes</p>
                <p className="text-2xl font-bold">{stats.seeking}</p>
              </div>
            </div>
          </div>
        
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-yellow-400" />
              <div className="ml-4">
                <p className="text-gray-400 text-sm">Most Popular Day Seeking</p>
                <p className="text-2xl font-bold">{stats.popularDay}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-700">
            <div className="flex space-x-1">
              <button
                onClick={() => setActiveTab('groups')}
                className={`px-6 py-3 font-medium text-sm transition-all relative ${
                  activeTab === 'groups' ? 'text-purple-400' : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Users size={18} />
                  <span>Group Matches</span>
                  {potentialGroups.length > 0 && (
                    <span className="ml-2 bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">
                      {potentialGroups.length}
                    </span>
                  )}
                </div>
                {activeTab === 'groups' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-600 to-indigo-500"></div>
                )}
              </button>

              <button
                onClick={() => setActiveTab('list')}
                className={`px-6 py-3 font-medium text-sm transition-all relative ${
                  activeTab === 'list' ? 'text-purple-400' : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Search size={18} />
                  <span>All Students</span>
                  <span className="ml-2 text-gray-500 text-xs">
                    ({students.length})
                  </span>
                </div>
                {activeTab === 'list' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-600 to-indigo-500"></div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'groups' ? (
          <div>
            {/* Group Formation Filters */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center">
                  <Filter size={16} className="mr-2 text-purple-400" />
                  Group Formation Criteria
                </h3>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setGroupFilters({
                    minGroupSize: 2,
                    maxGroupSize: 4,
                    minSharedSlots: 2,
                    strictLevelMatch: true,
                    classType: 'all'
                  })}
                >
                  Reset
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">
                    Group Size
                  </label>
                  <select
                    value={`${groupFilters.minGroupSize}-${groupFilters.maxGroupSize}`}
                    onChange={(e) => {
                      const [min, max] = e.target.value.split('-').map(Number);
                      setGroupFilters(prev => ({
                        ...prev,
                        minGroupSize: min,
                        maxGroupSize: max
                      }));
                    }}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
                  >
                    <option value="2-2">Pairs only (2)</option>
                    <option value="2-3">2-3 students</option>
                    <option value="2-4">2-4 students</option>
                    <option value="3-4">3-4 students</option>
                    <option value="3-6">3-6 students</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-1 block">
                    Minimum Shared Slots
                  </label>
                  <select
                    value={groupFilters.minSharedSlots}
                    onChange={(e) => handleGroupFilterChange('minSharedSlots', Number(e.target.value))}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
                  >
                    <option value="1">At least 1 slot</option>
                    <option value="2">At least 2 slots</option>
                    <option value="3">At least 3 slots</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-1 block">
                    Level Matching
                  </label>
                  <select
                    value={groupFilters.strictLevelMatch ? 'strict' : 'flexible'}
                    onChange={(e) => handleGroupFilterChange('strictLevelMatch', e.target.value === 'strict')}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
                  >
                    <option value="strict">Exact level only</option>
                    <option value="flexible">Within 1 level</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-1 block">
                    Class Type
                  </label>
                  <select
                    value={groupFilters.classType}
                    onChange={(e) => handleGroupFilterChange('classType', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
                  >
                    <option value="all">All Types</option>
                    {classTypes.map(ct => (
                      <option key={ct.$id} value={ct.$id}>{ct.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Priority Legend */}
            {potentialGroups.length > 0 && (
              <div className="flex items-center justify-between text-sm mb-6">
                <p className="text-gray-400">
                  Found {potentialGroups.length} potential group{potentialGroups.length !== 1 ? 's' : ''}
                </p>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                    <span className="text-gray-400">Ready to form</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                    <span className="text-gray-400">Almost ready</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                    <span className="text-gray-400">Possible</span>
                  </div>
                </div>
              </div>
            )}

            {/* Groups List */}
            {potentialGroups.length === 0 ? (
              <div className="text-center py-16 bg-gray-800 rounded-lg border border-gray-700">
                <Users size={64} className="text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-300 mb-2">
                  No Compatible Groups Found
                </h3>
                <p className="text-gray-400 mb-6">
                  Try adjusting your group formation criteria above
                </p>
                <Button
                  variant="outline"
                  onClick={() => setGroupFilters({
                    minGroupSize: 2,
                    maxGroupSize: 4,
                    minSharedSlots: 1,
                    strictLevelMatch: false,
                    classType: 'all'
                  })}
                >
                  Use More Flexible Criteria
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {potentialGroups.map((group, idx) => (
                  <GroupCard
                    key={idx}
                    group={group}
                    onExpand={() => toggleExpand(idx)}
                    isExpanded={expandedGroups[idx]}
                    onCreateGroup={() => handleCreateGroup(group)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {/* Filters */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-gray-400" />
                  <select
                    value={filters.classType}
                    onChange={(e) => handleFilterChange('classType', e.target.value)}
                    className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
                  >
                    <option value="all">All Class Types</option>
                    {classTypes.map(ct => (
                      <option key={ct.$id} value={ct.$id}>{ct.name}</option>
                    ))}
                  </select>
                </div>

                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white"
                >
                  <option value="all">All Status</option>
                  <option value="available">Available</option>
                  <option value="enrolled">In Class</option>
                  <option value="long_wait">Long Wait</option>
                </select>

                <div className="flex-1 relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search students..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400"
                  />
                </div>
              </div>
            </div>

            {/* Students List */}
            <div className="space-y-4">
              {filteredStudents.length === 0 ? (
                <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
                  <Users size={48} className="text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg mb-2">No students found</p>
                  {filters.search || filters.classType !== 'all' || filters.status !== 'all' ? (
                    <div>
                      <p className="text-gray-500 mb-4">Try adjusting your filters</p>
                      <Button
                        variant="outline"
                        onClick={() => setFilters({ classType: 'all', status: 'all', search: '' })}
                        className="border-gray-600 hover:bg-gray-700"
                      >
                        Clear Filters
                      </Button>
                    </div>
                  ) : (
                    <p className="text-gray-500">No students have submitted availability yet</p>
                  )}
                </div>
              ) : (
                filteredStudents.map((student) => {
                  const statusDisplay = getStatusDisplay(student.status, student.daysSinceSubmitted);
                  
                  // Helper to sanitize phone for wa.me links
                  const getWhatsAppLink = (phone) => {
                    if (!phone) return '';
                    // Removes all non-digit characters
                    const digitsOnly = phone.replace(/\D/g, '');
                    return `https://wa.me/${digitsOnly}`;
                  };
                
                  // Group availabilities by day
                  const groupedAvailability = student.availabilities.reduce((acc, slot) => {
                    const [day, time] = slot.split('-');
                    if (!acc[day]) {
                      acc[day] = [];
                    }
                    // formatTimeSlot is your existing utility function
                    acc[day].push(formatTimeSlot(slot).split(' at ')[1]);
                    return acc;
                  }, {});
                
                  return (
                    <div
                      key={student.availabilityId}
                      className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6 hover:border-gray-600 transition-colors"
                    >
                      {/* Card Header: Name and Status */}
                      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-700">
                        <h3 className="text-xl font-semibold text-white flex items-center">
                          <Users size={20} className="mr-3 text-purple-400" />
                          {student.name}
                        </h3>
                        <span className={`text-sm font-medium ${statusDisplay.color} flex items-center`}>
                          <span className="mr-2">{statusDisplay.icon}</span>
                          {statusDisplay.text}
                        </span>
                      </div>
                
                      {/* Main Content Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Left Column: Details & Actions */}
                        <div className="flex flex-col justify-between">
                          <div>
                            <div className="mb-4">
                              <p className="flex items-center text-purple-300 text-sm mb-1">
                                <BookOpen size={14} className="inline mr-2" />
                                Interested In
                              </p>
                              <p className="ml-6 text-gray-200">{student.classTypeName}</p>
                            </div>

                            <div className="mb-4">
                              <p className="flex items-center text-purple-300 text-sm mb-1">
                                <Target size={14} className="inline mr-2" />
                                Learning Goal
                              </p>
                              <p className="ml-6 text-gray-200">
                                {getGoalDisplay(student)}
                              </p>
                            </div>
                            
                            <div className="mb-4">
                               <p className="flex items-center text-gray-400 text-sm mb-1">
                                <Mail size={14} className="inline mr-2" />
                                {student.email}
                              </p>
                            </div>
                
                            <div>
                              <p className="flex items-center text-gray-400 text-sm mb-1">
                                <Phone size={14} className="inline mr-2" />
                                {student.phone || 'No phone number'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="mt-4 pt-4 border-t border-gray-700 flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="small"
                              onClick={() => window.open(`tel:${student.phone}`, '_self')}
                              className="text-green-400 hover:text-green-300 flex items-center"
                              disabled={!student.phone}
                            >
                              <Phone size={14} className="mr-1" />
                              Call
                            </Button>
                            <Button
                              variant="ghost"
                              size="small"
                              onClick={() => window.open(`mailto:${student.email}`, '_blank')}
                              className="text-blue-400 hover:text-blue-300 flex items-center"
                            >
                              <Mail size={14} className="mr-1" />
                              Email
                            </Button>
                            <Button
                              variant="ghost"
                              size="small"
                              onClick={() => window.open(getWhatsAppLink(student.phone), '_blank')}
                              className="text-teal-400 hover:text-teal-300 flex items-center"
                              disabled={!student.phone}
                            >
                              <MessageSquare size={14} className="mr-1" />
                              WhatsApp
                            </Button>
                          </div>
                        </div>
                
                        {/* Right Column: Availability */}
                        <div>
                          <p className="flex items-center text-purple-300 text-sm mb-2">
                            <Clock size={14} className="inline mr-2" />
                            Availability
                          </p>
                          <div className="space-y-2">
                            {Object.entries(groupedAvailability).map(([day, times]) => (
                              <div key={day}>
                                <p className="text-gray-300 font-medium">{day}</p>
                                <p className="text-gray-400 ml-2">
                                  <span className="font-mono">└─</span> {times.join(', ')}
                                </p>
                              </div>
                            ))}
                            {student.availabilities.length === 0 && (
                              <p className="text-gray-500 text-sm">No specific times listed.</p>
                            )}
                          </div>
                        </div>
                      </div>
                
                      {/* Card Footer: Added Date */}
                      <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between">
                        <button
                          data-delete-btn
                          onClick={() => handleDeleteStudent(student)}
                          disabled={deletingId === student.availabilityId}
                          className={`flex items-center text-xs px-3 py-1.5 rounded transition-all ${
                            pendingDeleteId === student.availabilityId
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : 'text-gray-500 hover:text-red-400'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          <Trash2 size={12} className="mr-1.5" />
                          {deletingId === student.availabilityId
                            ? 'Deleting...'
                            : pendingDeleteId === student.availabilityId
                            ? 'Confirm Delete'
                            : 'Delete'}
                        </button>

                        <p className="text-gray-500 text-xs">
                          Added: {formatDate(student.submittedAt)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Results summary */}
            {filteredStudents.length > 0 && (
              <div className="mt-6 text-center text-gray-400 text-sm">
                Showing {filteredStudents.length} of {students.length} students
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default StudentsPage;